import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { PageContainer, PageHeader, Card, CardHeader, StatCard, Table, Thead, Th, Td, TRow, Button, FormGroup, Input, Select, EmptyState, Badge, Muted } from "../ui";

interface PettyCashEntry {
  id: string;
  amount: number;
  entry_type: string;
  category: string | null;
  description: string;
  vendor_name: string | null;
  receipt_noted: boolean;
  running_balance: number;
  created_by_name: string;
  created_at: string;
}

interface PettyCashSettings {
  fund_balance: number;
  fund_limit: number;
}

const CATEGORIES = [
  "Supplies",
  "Cleaning",
  "Maintenance",
  "Food / Beverage",
  "Office",
  "Delivery / Shipping",
  "Tips",
  "Miscellaneous",
];

export default function PettyCashManager() {
  const [settings, setSettings] = useState<PettyCashSettings | null>(null);
  const [entries, setEntries] = useState<PettyCashEntry[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    amount: "",
    entry_type: "expense",
    category: "",
    description: "",
    vendor_name: "",
    receipt_noted: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const [settingsRes, entriesRes] = await Promise.all([
      api.get("/petty-cash/settings"),
      api.get("/petty-cash/entries?limit=100"),
    ]);
    setSettings(settingsRes.data);
    setEntries(entriesRes.data);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await api.post("/petty-cash/entries", {
      amount: Number(form.amount),
      entry_type: form.entry_type,
      category: form.category || null,
      description: form.description,
      vendor_name: form.vendor_name || null,
      receipt_noted: form.receipt_noted,
    });
    setForm({ amount: "", entry_type: "expense", category: "", description: "", vendor_name: "", receipt_noted: false });
    setShowForm(false);
    loadData();
  };

  const balance = Number(settings?.fund_balance || 0);
  const limit = Number(settings?.fund_limit || 200);
  const balancePercent = limit > 0 ? Math.round((balance / limit) * 100) : 0;

  return (
    <PageContainer>
      <PageHeader
        title="Petty Cash"
        subtitle="Track cash fund deposits, withdrawals, and expenses"
        action={
          <Button onClick={() => setShowForm(!showForm)}>
            {showForm ? "Cancel" : "+ New Entry"}
          </Button>
        }
      />

      {/* Balance overview */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard
          label="Current Balance"
          value={`$${balance.toFixed(2)}`}
          detail={`Fund limit: $${limit.toFixed(2)}`}
          variant={balance < 20 ? "danger" : balance < 50 ? "gold" : "green"}
        />
        <StatCard
          label="Fund Status"
          value={`${balancePercent}%`}
          detail={balance < 20 ? "Running low - needs replenishment" : "Healthy"}
          variant="default"
        />
        <StatCard
          label="Entries This Month"
          value={String(entries.filter((e) => {
            const d = new Date(e.created_at);
            const now = new Date();
            return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
          }).length)}
          detail="Total transactions"
          variant="blue"
        />
      </div>

      {/* New entry form */}
      {showForm && (
        <Card className="mb-6">
          <CardHeader title="New Petty Cash Entry" />
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <FormGroup label="Type">
                <Select value={form.entry_type} onChange={(val) => setForm({ ...form, entry_type: val })}>
                  <option value="expense">Expense (money out)</option>
                  <option value="withdrawal">Withdrawal (money out)</option>
                  <option value="deposit">Deposit (money in)</option>
                </Select>
              </FormGroup>
              <FormGroup label="Amount">
                <Input
                  type="number"
                  value={form.amount}
                  onChange={(val) => setForm({ ...form, amount: val })}
                  placeholder="0.00"
                  step="0.01"
                  min="0.01"
                  required
                />
              </FormGroup>
              <FormGroup label="Category">
                <Select value={form.category} onChange={(val) => setForm({ ...form, category: val })}>
                  <option value="">Select category...</option>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </Select>
              </FormGroup>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <FormGroup label="Description" span={1}>
                <Input
                  value={form.description}
                  onChange={(val) => setForm({ ...form, description: val })}
                  placeholder="What was this for?"
                  required
                />
              </FormGroup>
              <FormGroup label="Vendor / Paid To">
                <Input
                  value={form.vendor_name}
                  onChange={(val) => setForm({ ...form, vendor_name: val })}
                  placeholder="Store name, person, etc."
                />
              </FormGroup>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.receipt_noted}
                  onChange={(e) => setForm({ ...form, receipt_noted: e.target.checked })}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm font-medium" style={{ color: "var(--rbw-navy)" }}>
                  Receipt on file
                </span>
              </label>
              <div className="flex-1" />
              <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" variant="primary">Save Entry</Button>
            </div>
          </form>
        </Card>
      )}

      {/* History */}
      {entries.length === 0 ? (
        <EmptyState
          icon="&#128176;"
          message="No petty cash entries yet"
          detail="Add your first entry to start tracking"
        />
      ) : (
        <Table>
          <Thead>
            <Th>Date</Th>
            <Th>Type</Th>
            <Th>Description</Th>
            <Th>Category</Th>
            <Th>Vendor</Th>
            <Th align="right">Amount</Th>
            <Th align="right">Balance</Th>
            <Th align="center">Receipt</Th>
            <Th>By</Th>
          </Thead>
          <tbody>
            {entries.map((entry, idx) => {
              const isOut = entry.entry_type !== "deposit";
              const typeBadge = entry.entry_type === "deposit" ? "green"
                : entry.entry_type === "expense" ? "danger"
                : "gold";

              return (
                <TRow key={entry.id} index={idx}>
                  <Td>
                    <div className="text-xs">
                      {new Date(entry.created_at).toLocaleDateString()}
                    </div>
                    <div className="text-xs"><Muted>{new Date(entry.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</Muted></div>
                  </Td>
                  <Td>
                    <Badge variant={typeBadge as "green" | "danger" | "gold"}>
                      {entry.entry_type}
                    </Badge>
                  </Td>
                  <Td bold>{entry.description}</Td>
                  <Td><Muted>{entry.category || "--"}</Muted></Td>
                  <Td><Muted>{entry.vendor_name || "--"}</Muted></Td>
                  <Td align="right" bold>
                    <span style={{ color: isOut ? "var(--rbw-danger)" : "var(--rbw-success)" }}>
                      {isOut ? "-" : "+"}${Number(entry.amount).toFixed(2)}
                    </span>
                  </Td>
                  <Td align="right" bold>${Number(entry.running_balance).toFixed(2)}</Td>
                  <Td align="center">
                    {entry.receipt_noted ? (
                      <span style={{ color: "var(--rbw-success)" }}>&#10003;</span>
                    ) : (
                      <Muted>--</Muted>
                    )}
                  </Td>
                  <Td><Muted>{entry.created_by_name}</Muted></Td>
                </TRow>
              );
            })}
          </tbody>
        </Table>
      )}
    </PageContainer>
  );
}
